/**
 * Self-contained "Preview" (eye) button + modal for a blog template. Drop one
 * onto any template card; it manages its own open state and stops the click
 * from bubbling to the card's select handler.
 */
import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { TemplatePreviewModal } from './TemplatePreviewModal';

interface TemplatePreviewTriggerProps {
  template: BlogTemplateSummary;
  onUse?: (templateId: string) => void;
  isSelected?: boolean;
  className?: string;
}

export const TemplatePreviewTrigger: React.FC<TemplatePreviewTriggerProps> = ({
  template,
  onUse,
  isSelected,
  className,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label={`Preview ${template.name}`}
        title="Preview template"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          'inline-flex items-center justify-center rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
      >
        <Eye className="h-4 w-4" />
      </button>
      <TemplatePreviewModal
        template={template}
        open={open}
        onOpenChange={setOpen}
        onUse={onUse}
        isSelected={isSelected}
      />
    </>
  );
};
